import { useEffect, useState } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValue,
    useSpring,
} from "framer-motion";
import { MessageCircle, ArrowRight, ShieldCheck } from "lucide-react";
import { WA_DEFAULT, goTo, IMAGES } from "@/lib/site";

const LINES = [
    { text: "SEGURANÇA", cls: "text-white" },
    { text: "QUE ENXERGA", cls: "text-outline" },
    { text: "MAIS LONGE.", cls: "text-mint" },
];

const lineAnim = (i) => ({
    initial: { y: "115%" },
    animate: { y: "0%" },
    transition: {
        duration: 0.95,
        ease: [0.22, 1, 0.36, 1],
        delay: 0.35 + i * 0.15,
    },
});

const HudBox = ({ label, className, delay }) => (
    <motion.div
        initial={{ opacity: 0, scale: 1.08 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        className={`absolute border border-volt/80 ${className}`}
    >
        <span className="absolute -top-5 left-0 font-hud text-[9px] uppercase tracking-[0.2em] text-volt bg-ink/80 px-1.5 py-0.5 whitespace-nowrap">
            {label}
        </span>
        <span className="absolute -top-px -left-px h-2 w-2 border-t-2 border-l-2 border-volt" />
        <span className="absolute -bottom-px -right-px h-2 w-2 border-b-2 border-r-2 border-volt" />
    </motion.div>
);

const Hero = () => {
    const { scrollY } = useScroll();
    const yBg = useTransform(scrollY, [0, 700], [0, 140]);
    const oBg = useTransform(scrollY, [0, 700], [1, 0.35]);

    const rotX = useMotionValue(0);
    const rotY = useMotionValue(0);
    const springX = useSpring(rotX, { stiffness: 110, damping: 16 });
    const springY = useSpring(rotY, { stiffness: 110, damping: 16 });

    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const onTilt = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        rotY.set(px * 8);
        rotX.set(-py * 8);
    };
    const resetTilt = () => {
        rotX.set(0);
        rotY.set(0);
    };

    return (
        <section className="relative min-h-screen overflow-hidden flex items-center pt-32 pb-24 lg:pt-36 lg:pb-28">
            <motion.div
                style={{ y: yBg, opacity: oBg }}
                className="absolute inset-0 -z-10"
            >
                <img
                    src={IMAGES.heroCamera}
                    alt=""
                    className="h-full w-full object-cover opacity-35 grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
                <div className="absolute inset-0 grid-bg opacity-60" />
            </motion.div>

            <div className="mx-auto max-w-7xl px-5 lg:px-8 w-full grid lg:grid-cols-12 gap-14 items-center">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        data-testid="hero-system-status-badge"
                        className="inline-flex items-center gap-2.5 rounded-full border border-mint/30 bg-mint/10 px-4 py-2"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-70" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-mint" />
                        </span>
                        <span className="font-hud text-[10px] uppercase tracking-[0.25em] text-mint">
                            Sistema online · vigilância ativa
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-main-title"
                        className="mt-7 font-display font-black uppercase leading-[0.95] tracking-tight text-[clamp(2.6rem,6.1vw,5.6rem)]"
                    >
                        {LINES.map((l, i) => (
                            <span
                                key={i}
                                className="block overflow-hidden pb-[0.09em] -mb-[0.09em]"
                            >
                                <motion.span
                                    className={`block ${l.cls}`}
                                    {...lineAnim(i)}
                                >
                                    {l.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.95 }}
                        className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
                    >
                        CFTV, controle de acesso facial, portões automatizados e
                        manutenção especializada para condomínios e residências.
                        Projeto sob medida, instalação limpa e suporte que
                        responde quando você mais precisa.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1.1 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contato"
                            data-testid="hero-cta-request-quote"
                            onClick={(e) => goTo(e, "#contato")}
                            className="group inline-flex items-center gap-3 rounded-full bg-volt px-7 py-4 font-hud text-xs font-bold uppercase tracking-[0.18em] text-ink hover:bg-mint transition-colors duration-200"
                        >
                            Solicitar orçamento
                            <ArrowRight
                                size={16}
                                className="transition-transform duration-200 group-hover:translate-x-1"
                            />
                        </a>
                        <a
                            href={WA_DEFAULT}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-cta-whatsapp-direct"
                            className="inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 font-hud text-xs font-bold uppercase tracking-[0.18em] text-slate-100 hover:border-volt hover:text-volt transition-colors duration-200"
                        >
                            <MessageCircle size={16} strokeWidth={2.4} />
                            WhatsApp direto
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.3 }}
                        className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-hud text-[10px] uppercase tracking-[0.22em] text-slate-400"
                    >
                        <span className="flex items-center gap-2">
                            <ShieldCheck size={13} className="text-volt" />
                            CFTV 4K
                        </span>
                        <span>· Acesso facial</span>
                        <span>· Portões e automação</span>
                        <span>· Suporte 24/7</span>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5"
                    style={{ perspective: 900 }}
                >
                    <motion.div
                        data-testid="hero-camera-feed"
                        onMouseMove={onTilt}
                        onMouseLeave={resetTilt}
                        style={{ rotateX: springX, rotateY: springY }}
                        className="relative clip-corner border border-white/10 bg-panel/80 backdrop-blur-sm shadow-[0_0_80px_rgba(46,124,246,0.14)]"
                    >
                        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
                            <span className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                CAM 07 · Portaria · 4K
                            </span>
                            <span className="flex items-center gap-2 font-hud text-[10px] uppercase tracking-[0.2em] text-mint">
                                <span className="h-1.5 w-1.5 rounded-full bg-mint blink-soft" />
                                Online
                            </span>
                        </div>
                        <div className="relative h-64 sm:h-80 overflow-hidden">
                            <img
                                src={IMAGES.cameraPole}
                                alt="Conjunto de câmeras de segurança instaladas"
                                className="h-full w-full object-cover grayscale contrast-110"
                            />
                            <div className="absolute inset-0 bg-volt/5 mix-blend-overlay" />
                            <div className="scanline" />
                            <HudBox label="Pessoa · 98%" className="left-[14%] top-[22%] h-24 w-16" delay={1.2} />
                            <HudBox label="Veículo · Rastreado" className="right-[12%] bottom-[16%] h-16 w-28" delay={1.45} />
                        </div>
                        <div className="flex items-center justify-between px-5 py-3.5 border-t border-white/10">
                            <span className="flex items-center gap-2 font-hud text-[11px] tracking-[0.2em] text-red-400">
                                <span className="h-2 w-2 rounded-full bg-red-500 blink-soft" />
                                REC {now.toLocaleTimeString("pt-BR", { hour12: false })}
                            </span>
                            <span className="font-hud text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Gravação local + nuvem
                            </span>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
